/**
 * Editorial DOM: minimal — hero, the pinned stage (containing floating caption),
 * outro section. Most of the visual weight lives in the 3D scene.
 */
import { brand, products } from '../../products.js';
import { el } from '../../core/utils.js';

export function editorialLayout(host, { embedded = false } = {}) {
  host.classList.add('tpl-editorial');
  if (embedded) host.classList.add('tpl-embedded');

  const nav = embedded ? null : el('header', { className: 'nav editorial-nav' },
    el('a', { className: 'logo', href: '#' }, brand.name),
    el('nav', {},
      el('a', { href: '#stage' }, 'Story'),
      el('a', { href: '#outro' }, 'Shop'),
    ),
  );

  const intro = embedded ? null : el('section', { className: 'editorial-intro' },
    el('div', { className: 'editorial-intro__copy' },
      el('p', { className: 'eyebrow' }, brand.hero.eyebrow),
      el('h1', {}, brand.hero.headline.replace(/<\/?em>/g, '')),
      el('p', { className: 'lede' }, brand.hero.lede),
      el('p', { className: 'editorial-hint' }, 'scroll to begin ↓'),
    ),
  );

  // Captions container — one caption per product, only one visible at a time.
  const captions = el('div', { className: 'editorial-captions' });
  const captionEls = products.map((p, i) =>
    el('article', {
      className: 'editorial-caption' + (i === 0 ? ' is-active' : ''),
      dataset: { index: String(i) },
    },
      el('span', { className: 'eyebrow', style: { color: p.accent } }, p.tag),
      el('h2', {}, p.name),
      el('p', { className: 'price' }, p.price),
      p.stripeLink
        ? el('a', { className: 'editorial-buy', href: p.stripeLink, target: '_blank', rel: 'noopener' }, 'Buy ' + p.name)
        : null,
    ),
  );
  captionEls.forEach((c) => c && captions.appendChild(c));

  const stage = el('section', { className: 'editorial-stage', id: 'stage' },
    captions,
    el('div', { className: 'editorial-progress' },
      ...products.map((_, i) => el('span', { className: 'editorial-pip' + (i === 0 ? ' is-active' : ''), dataset: { index: String(i) } })),
    ),
  );

  const outro = embedded ? null : el('section', { className: 'editorial-outro', id: 'outro' },
    el('h2', {}, brand.manifesto.title.replace(/\n/g, ' / ')),
    el('p', {}, brand.manifesto.body),
    el('footer', {}, '© ' + brand.name),
  );

  const main = el('main', { className: 'tpl-editorial-main' + (embedded ? ' tpl-editorial-main--embedded' : '') });
  if (intro) main.appendChild(intro);
  main.appendChild(stage);
  if (outro) main.appendChild(outro);
  if (nav) host.appendChild(nav);
  host.appendChild(main);

  function setCaption(idx) {
    captionEls.forEach((c, i) => {
      if (!c) return;
      c.classList.toggle('is-active', i === idx);
    });
    stage.querySelectorAll('.editorial-pip').forEach((p, i) => {
      p.classList.toggle('is-active', i === idx);
    });
  }

  return {
    setCaption,
    cleanup() {
      host.classList.remove('tpl-editorial', 'tpl-embedded');
      nav?.remove();
      main.remove();
    },
  };
}
